import React, { useState, useRef, useEffect } from "react";
import { OverlayTrigger, Popover, Table } from "react-bootstrap";

function DaysTooltip({ materials }) {
  const target = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (show && target.current) {
        const targetRect = target.current.getBoundingClientRect();
        const popoverWidth = 600;
        const windowWidth = window.innerWidth;
        const scrollX = window.scrollX || window.pageXOffset;
        let placement = "right";
        let x = targetRect.right + scrollX;

        if (x + popoverWidth > windowWidth + scrollX) {
          placement = "left";
          x = targetRect.left + scrollX - popoverWidth;
        }

        if (x < scrollX) x = scrollX + 10;
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, [show]);

  // Проверка на отсутствие данных
  if (!materials || materials.length === 0) {
    return <span>—</span>;
  }

  // Вычисляем данные о днях для каждого материала
  const daysData = materials
    .filter(m => m.date && m.date !== "Нет данных" && m.date !== "—")
    .map(material => {
      const daysCount = Math.max(1,
        Math.floor(
          (new Date() - new Date(material.date.split('.').reverse().join('-'))) / 
          (1000 * 60 * 60 * 24)
        )
      );
      return {
        name: material.name,
        days: daysCount,
        date: material.date
      };
    });

  // Определяем уникальные значения дней
  const uniqueDays = Array.from(new Set(daysData.map(item => item.days)));
  const isDifferentDays = uniqueDays.length > 1;
  const displayValue = isDifferentDays ? "Разное" : (uniqueDays.length === 1 ? uniqueDays[0] : "—");
  
  // Проверяем, есть ли цены старше 14 дней
  const hasOldPrices = daysData.some(item => item.days > 14);
  
  // Определяем стиль для выделения старых цен
  const getTextStyle = () => {
    const baseStyle = {
      cursor: isDifferentDays ? "pointer" : "default",
      textDecoration: isDifferentDays ? "underline" : "none",
    };
    
    if (hasOldPrices) {
      return {
        ...baseStyle,
        color: "#b97a0c", // Янтарный warning под дизайн-палитру (amber)
        fontWeight: 600,
      };
    }

    return {
      ...baseStyle,
      color: isDifferentDays ? "#005ca9" : "#7a8aa3", // Синий для кликабельных, dim-серый для остальных
    };
  };

  // Если нет данных о датах, показываем обычное значение
  if (daysData.length === 0) {
    return <span>{displayValue}</span>;
  }

  // Если все дни одинаковые, показываем значение без tooltip, но с цветовым выделением
  if (!isDifferentDays) {
    return <span style={getTextStyle()}>{displayValue}</span>;
  }

  const popover = (
    <Popover
      id="days-popover"
      style={{
        maxWidth: "90vw",
        minWidth: "400px",
        maxHeight: "70vh",
        overflow: "auto",
      }}
    >
      <Popover.Header as="h5">Детали по дням цене</Popover.Header>
      <Popover.Body>
        <Table striped bordered hover size="sm" style={{ width: "100%", tableLayout: "auto" }}>
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>Номенклатура</th>
              <th style={{ whiteSpace: "nowrap" }}>Сколько дней</th>
              <th style={{ whiteSpace: "nowrap" }}>Дата обновления</th>
            </tr>
          </thead>
          <tbody>
            {daysData.map((item, index) => {
              const isOldPrice = item.days > 14;
              const rowStyle = isOldPrice ? { backgroundColor: "#fff7e6" } : {}; // Янтарный фон для старых цен
              const textStyle = isOldPrice ? { color: "#b97a0c", fontWeight: 600 } : {}; // Янтарный текст для старых цен
              
              return (
                <tr key={index} style={rowStyle}>
                  <td style={{ wordBreak: "break-word", ...textStyle }}>{item.name}</td>
                  <td style={{ textAlign: "center", ...textStyle }}>{item.days}</td>
                  <td style={{ textAlign: "center", ...textStyle }}>{item.date}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="auto"
      overlay={popover}
      rootClose
      show={show}
      onToggle={setShow}
      popperConfig={{
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 10],
            },
          },
        ],
      }}
    >
      <span
        ref={target}
        onClick={() => setShow(!show)}
        style={getTextStyle()}
      >
        {displayValue}
      </span>
    </OverlayTrigger>
  );
}

export default DaysTooltip;